import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pc, pcs, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; skank: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  C: { root: 'C2', skank: ['G3', 'C4', 'E4', 'G4'] },
  F: { root: 'F1', skank: ['A3', 'C4', 'F4', 'A4'] },
  G: { root: 'G1', skank: ['G3', 'B3', 'D4', 'G4'] },
  Am: { root: 'A1', skank: ['A3', 'C4', 'E4', 'A4'] },
  Dm: { root: 'D2', skank: ['A3', 'D4', 'F4', 'A4'] },
};

const PROGS = [
  ['C', 'F', 'G', 'F'],
  ['C', 'Am', 'F', 'G'],
  ['F', 'G', 'C', 'C'],
  ['C', 'G', 'Am', 'F'],
  ['Dm', 'G', 'C', 'C'],
] as const;

const HOOK: Note[][] = [
  [[0, 'E5', 2], [2, 'G5', 2], [4, 'A5', 3], [7, 'G5', 1, 0.75], [8, 'E5', 2], [10, 'D5', 2], [12, 'C5', 4]],
  [[2, 'C5', 1, 0.75], [3, 'D5', 1, 0.8], [4, 'E5', 2], [6, 'G5', 2], [8, 'A5', 2], [10, 'G5', 2], [12, 'E5', 4]],
  [[0, 'F5', 2], [2, 'A5', 2], [4, 'C6', 3], [7, 'A5', 1, 0.75], [8, 'G5', 2], [10, 'F5', 2], [12, 'E5', 4]],
  [[0, 'D5', 2], [2, 'E5', 2], [4, 'G5', 2], [6, 'E5', 2], [8, 'D5', 4], [12, 'C5', 4]],
];

const KEYS = [0, 2, -3, -2, 3, -5, 1] as const;
const MAJOR: readonly number[] = [0, 2, 4, 5, 7, 9, 11];
const FUNDOS = ['X.......X.......', 'X.......X.....x.'] as const;
const SEGUNDAS = ['....X.......X...', '....X.......X.x.'] as const;
const DOBRAS = ['..x..xX...x..xX.', '..x..x.x..x..xX.'] as const;
const MUTES = '....o.......o...';
const CLAVES = ['X..X..X...X.X...', 'X..X...X..X.X...'] as const;
const TIMBALS = ['X..o.oX...o.X.o.', 'X.oo..X.o.o.X...'] as const;
const REPIQUES = ['..x...x...x...x.', '...x..x....x..x.'] as const;
const SHAKER = 'x-x-X-x-x-x-X-x-';
const SKANK = [2, 6, 10, 14] as const;
const BASSES = [
  [[0, 0, 3], [3, 0, 1], [6, 7, 2], [8, 0, 3], [11, 12, 1], [14, 7, 2]],
  [[0, 0, 4], [6, 7, 2], [8, 0, 2], [10, 12, 2], [14, 7, 2]],
] as const;
const CHAMADA = '........X.X.XXXX';
const VIRADA = 'X..X..X.X.X.XXXX';

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 3 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 2 },
  ],
};

function third(note: string): string {
  const m = midi(note);
  for (const d of [3, 4]) if (MAJOR.includes(pc(m - d))) return noteName(m - d);
  return noteName(m - 3);
}

function score(seed: string, p: Params): Score {
  const ch = chooser(SAMBAREGGAE, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const fundo = ch.pick(FUNDOS);
  const segunda = ch.pick(SEGUNDAS);
  const dobra = ch.pick(DOBRAS);
  const clave = ch.pick(CLAVES);
  const timbal = ch.pick(TIMBALS);
  const repique = ch.pick(REPIQUES);
  const bassRow = ch.pick(BASSES);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs(c.skank) }]),
        [...MAJOR],
        [69, 86],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    let m = midi(tr(n));
    while (m < 31) m += 12;
    while (m > 42) m -= 12;
    return noteName(m);
  };
  const claveSteps = hits(clave).map((h) => h.step);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 52, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const rng = createRng(`sambareggae-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });
  const pattern = (v: string, bar: number, pat: string, scale: number, from = 0) => {
    for (const h of hits(pat)) if (h.step >= from) hit(v, bar, h.step, human(h.vel * scale));
  };
  const timbals = (bar: number, scale: number) => {
    for (const h of hits(timbal)) hit(h.vel >= 0.75 ? 'slap' : 'timbal', bar, h.step, human((h.vel >= 0.75 ? h.vel : 0.75) * scale));
  };
  const skanks = (bar: number, notes: readonly string[], vel: number) => {
    for (const st of SKANK) chord('skank', bar, st, notes, s * 1.3, human(vel * (st === 6 || st === 14 ? 1 : 0.85)), 0.007);
  };
  const bass = (bar: number, root: string, vel: number) => {
    for (const [st, semis, len] of bassRow) play('bass', bar, st, transpose(root, semis), len * s * 0.92, human(vel * (st % 8 === 0 ? 1 : 0.82)));
  };
  const apito = (bar: number, step: number) => {
    play('apito', bar, step, 'D7', s * 0.9, 0.8);
    play('apito', bar, step + 2, 'D7', s * 1.8, 0.8);
  };

  const autos: Score['autos'] = [
    { v: 'bandLp', points: [[g.at(0), 800], [g.at(intro), 6500]] },
    { v: 'bandLp', points: [[g.at(total - 1), 6500], [g.at(total), 1600]] },
  ];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? prog[0]! : prog[i]!;
    const drop = b.name === 'drop' || b.name === 'drop2';
    const groove = drop || b.name === 'A' || (b.name === 'outro' && b.index < b.bars - 2);
    const root = low(c.root);
    const notes = c.skank.map(tr);

    if (end) {
      hit('surdo1', b.bar, 0, 1);
      hit('surdo2', b.bar, 0, 0.9);
      hit('clave', b.bar, 0, 1);
      hit('impact', b.bar, 0, 0.8);
      hit('slap', b.bar, 0, 0.9);
      chord('skank', b.bar, 0, notes, g.barLength * 0.8, 0.8, 0.02);
      play('bass', b.bar, 0, root, g.barLength * 0.7, 0.9);
      continue;
    }

    if (groove) {
      const fill = b.last && b.name === 'A';
      pattern('surdo1', b.bar, fundo, 1);
      pattern('surdo2', b.bar, segunda, 0.95);
      pattern('mute', b.bar, MUTES, 1);
      if (drop || e >= 0.6) pattern('dobra', b.bar, dobra, 0.9);
      pattern('clave', b.bar, clave, drop ? 1 : 0.9);
      if (drop) for (let st = 0; st < 16; st++) if (!claveSteps.includes(st)) hit('caixa', b.bar, st, human(st % 2 === 0 ? 0.32 : 0.24));
      if (drop || e >= 0.7) timbals(b.bar, drop ? 1 : 0.8);
      if (fill) pattern('repique', b.bar, CHAMADA, 1);
      else if (drop) pattern('repique', b.bar, repique, 0.8);
      pattern('shaker', b.bar, SHAKER, drop ? 1 : 0.85);
      skanks(b.bar, notes, drop ? 0.8 : 0.7);
      bass(b.bar, root, drop ? 1 : 0.9);
    } else if (b.name === 'break') {
      if (b.index === 0) apito(b.bar, 0);
      timbals(b.bar, 1);
      pattern('repique', b.bar, b.index % 2 === 0 ? repique : CHAMADA, 0.9);
      pattern('shaker', b.bar, SHAKER, 0.9);
      skanks(b.bar, notes, 0.65);
      if (b.index >= 2) bass(b.bar, root, 0.85);
      if (b.last) {
        pattern('surdo1', b.bar, VIRADA, 0.95, 8);
        pattern('clave', b.bar, VIRADA, 0.9, 8);
        apito(b.bar, 8);
      }
    } else if (b.name === 'intro') {
      pattern('shaker', b.bar, SHAKER, 0.7);
      skanks(b.bar, notes, 0.6);
      if (b.index >= 1) for (const h of hits(timbal)) if (h.vel < 0.75) hit('timbal', b.bar, h.step, human(0.6));
      if (b.index >= b.bars / 2) bass(b.bar, root, 0.75);
      if (b.last) {
        pattern('repique', b.bar, CHAMADA, 1);
        pattern('surdo1', b.bar, '........X...X.X.', 0.85);
      }
    } else if (b.name === 'outro') {
      pattern('shaker', b.bar, SHAKER, 0.6);
      skanks(b.bar, notes, 0.55);
      pattern('surdo1', b.bar, 'X.......', 0.8);
      play('bass', b.bar, 0, root, s * 6, 0.7);
    }

    const row = hook[i]!;
    const lead = (vel: number, horns: boolean) => {
      for (const [step, note, len, v] of row) {
        play('lead', b.bar, step, tr(note), len * s * 0.85, human((v ?? 0.95) * vel));
        if (horns) play('horn', b.bar, step, tr(third(note)), len * s * 0.85, (v ?? 0.9) * vel * 0.75);
      }
    };
    if (b.name === 'drop') lead(1, false);
    if (b.name === 'drop2') lead(1, true);
    if (b.name === 'A' && e >= 0.75 && i >= 2) lead(0.75, false);
  }

  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('impact', bar, 0, 1);
    if (bar > 0) apito(bar - 1, 12);
  }

  return {
    id: trackId(SAMBAREGGAE, seed, p),
    template: SAMBAREGGAE.id,
    seed,
    params: p,
    title: ch.orig ? SAMBAREGGAE.ref.title : titleOf(seed),
    genre: SAMBAREGGAE.genre,
    bpm: p.bpm,
    swing: 52,
    lufs: -11,
    key,
    form,
    mix: { reverbDecay: 1.6, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.3, drive: 1.4, softness: 1.3, pumpDepth: 0.2, pumpRelease: 0.24 },
    events,
    autos,
    accents: { surdo1: 'kick', surdo2: 'kick', clave: 'snare', skank: 'stab', lead: 'melody', impact: 'drop' },
  };
}

export const SAMBAREGGAE: Template = {
  id: 'sambareggae',
  genre: 'samba-reggae',
  bpm: [116, 134],
  ref: { seed: 'calcadao', title: 'Calçadão', params: { bpm: 122, bars: 28, energy: 0.55 } },
  score,
};
